import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';
import { FaPen, FaUpload, FaTrash, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '@/styles/SignatureUpload.scss';

/**
 * SignatureUpload Component
 * Allows users to create and upload signatures using drawing canvas or image upload.
 *
 * Layout improvements:
 *  - Draw mode shows a clearly labelled bordered canvas area with a corner label
 *    so the user immediately understands where to draw their signature.
 *  - Mode-selector tabs sit at the top; action buttons (Clear / Save) sit below the
 *    canvas so they are visually grouped with the content they act on.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onSignatureSave - Callback when signature is saved
 * @param {string} props.existingSignature - Existing signature data (base64)
 */
const SignatureUpload = ({ onSignatureSave, existingSignature }) => {
    const signatureCanvasRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'upload'
    const [uploadedImage, setUploadedImage] = useState(null);
    const [hasDrawn, setHasDrawn] = useState(false);

    /**
     * Resize the underlying <canvas> element so its internal pixel resolution
     * matches the CSS display size. Without this the browser scales the fixed
     * 500×200 canvas to fill the container, which causes:
     *  - Drawing offset (pointer X/Y maps to the wrong canvas pixel)
     *  - Thick / blurry lines (the canvas bitmap is stretched)
     */
    const resizeCanvas = useCallback(() => {
        const sigCanvas = signatureCanvasRef.current;
        if (!sigCanvas) return;

        const canvas = sigCanvas.getCanvas();
        // Read the CSS display size of the canvas element itself.
        // Do NOT read the container — its height is fixed at 200px via CSS
        // and the canvas fills it; reading the container could cause a
        // feedback loop where setting canvas dimensions grows the container.
        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;
        if (!displayWidth || !displayHeight) return;

        const ratio = window.devicePixelRatio || 1;
        const targetW = Math.round(displayWidth * ratio);
        const targetH = Math.round(displayHeight * ratio);

        // Only resize when the dimensions actually changed
        if (canvas.width === targetW && canvas.height === targetH) {
            return;
        }

        canvas.width = targetW;
        canvas.height = targetH;
        // Do NOT set canvas.style.width / canvas.style.height —
        // CSS already controls the display size (width:100%, height:100%).

        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);

        // Clearing is unavoidable on resize — the canvas bitmap is replaced
        sigCanvas.clear();
    }, []);

    // Observe container size changes so the canvas resolution stays in sync
    useEffect(() => {
        if (signatureMode !== 'draw') return;

        // Defer initial sizing so the container has its final layout dimensions
        const timer = setTimeout(resizeCanvas, 0);

        const container = canvasContainerRef.current;
        let observer;
        if (container && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(resizeCanvas);
            observer.observe(container);
        }

        return () => {
            clearTimeout(timer);
            observer?.disconnect();
        };
    }, [signatureMode, resizeCanvas]);

    useEffect(() => {
        if (existingSignature) {
            const timer = setTimeout(() => {
                setUploadedImage(existingSignature);
                setSignatureMode('upload');
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [existingSignature]);

    /**
     * Returns a data-URL of the drawn signature cropped to its bounding box
     * with a small padding margin, removing all surrounding whitespace.
     */
    const getCroppedCanvasDataURL = () => {
        const canvas = signatureCanvasRef.current.getCanvas();
        const ctx = canvas.getContext('2d');
        const { width, height } = canvas;
        const imageData = ctx.getImageData(0, 0, width, height);
        const { data } = imageData;

        // Scan for the bounding box of non-transparent pixels
        let minX = width, minY = height, maxX = 0, maxY = 0;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const alpha = data[(y * width + x) * 4 + 3];
                if (alpha > 0) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
        }

        // Nothing drawn — fall back to full canvas
        if (maxX < minX || maxY < minY) {
            return canvas.toDataURL('image/png');
        }

        // Add a small padding (in canvas-resolution pixels) so the stroke
        // edges aren't clipped right at the bounding box.
        const padding = Math.round(10 * (window.devicePixelRatio || 1));
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(width - 1, maxX + padding);
        maxY = Math.min(height - 1, maxY + padding);

        const cropW = maxX - minX + 1;
        const cropH = maxY - minY + 1;

        // Draw the cropped region onto a temporary canvas
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = cropW;
        tmpCanvas.height = cropH;
        const tmpCtx = tmpCanvas.getContext('2d');
        tmpCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

        return tmpCanvas.toDataURL('image/png');
    };

    const handleClear = () => {
        if (signatureMode === 'draw' && signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
            setHasDrawn(false);
        } else if (signatureMode === 'upload') {
            setUploadedImage(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleSave = () => {
        let signatureData = null;
        let signatureType = 'Drawn';

        if (signatureMode === 'draw' && signatureCanvasRef.current) {
            if (!hasDrawn || signatureCanvasRef.current.isEmpty()) {
                toast.error('Please draw your signature first');
                return;
            }
            signatureData = getCroppedCanvasDataURL();
        } else if (signatureMode === 'upload' && uploadedImage) {
            signatureData = uploadedImage;
            signatureType = 'Image';
        }

        if (signatureData) {
            onSignatureSave({
                signatureData,
                signatureType,
                contentType: 'image/png',
                isPrimary: true
            });
        } else {
            toast.error('No signature to save');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.match('image.*')) {
            toast.error('Please upload an image file');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image size must be less than 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setUploadedImage(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleBeginDrawing = () => {
        setHasDrawn(true);
    };

    return (
        <div className="signature-upload">
            {/* ── Mode selector tabs ── */}
            <div className="signature-mode-selector">
                <button
                    type="button"
                    className={`mode-btn ${signatureMode === 'draw' ? 'active' : ''}`}
                    onClick={() => setSignatureMode('draw')}
                >
                    <FaPen /> Draw Signature
                </button>
                <button
                    type="button"
                    className={`mode-btn ${signatureMode === 'upload' ? 'active' : ''}`}
                    onClick={() => setSignatureMode('upload')}
                >
                    <FaUpload /> Upload Image
                </button>
            </div>

            {/* ── Canvas / upload area ── */}
            <div className="signature-content">
                {signatureMode === 'draw' ? (
                    <div className="signature-canvas-container" ref={canvasContainerRef}>
                        {/* Corner label so the user knows this is the drawing area */}
                        <span className="canvas-corner-label">Sign here</span>
                        <SignatureCanvas
                            ref={signatureCanvasRef}
                            canvasProps={{
                                className: 'signature-canvas'
                            }}
                            onBegin={handleBeginDrawing}
                        />
                        {/* Centred placeholder hint shown before any strokes */}
                        <div className="canvas-placeholder">
                            {!hasDrawn && 'Draw your signature above this line'}
                        </div>
                        {/* Horizontal baseline acts as a visual guide for where to sign */}
                        <div className="signature-baseline" aria-hidden="true">
                            <span className="baseline-x">✕</span>
                        </div>
                    </div>
                ) : (
                    <div className="signature-upload-container">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />
                        {uploadedImage ? (
                            <div className="uploaded-signature-preview">
                                <img src={uploadedImage} alt="Signature" />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="upload-trigger-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <FaUpload size={24} />
                                <span>Click to upload signature image</span>
                                <small>(PNG, JPG, max 2MB)</small>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Action buttons – clear and save grouped below the canvas ── */}
            <div className="signature-actions">
                <button
                    type="button"
                    className="clear-btn"
                    onClick={handleClear}
                    title="Clear the current signature"
                >
                    <FaTrash /> Clear
                </button>
                <button
                    type="button"
                    className="save-btn"
                    onClick={handleSave}
                    title="Save this signature to your profile"
                >
                    <FaSave /> Save Signature
                </button>
            </div>
        </div>
    );
};

SignatureUpload.propTypes = {
    onSignatureSave: PropTypes.func.isRequired,
    existingSignature: PropTypes.string
};

export default SignatureUpload;
